import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { API_BASE_URL as BASE_URL } from '../services/apiBaseUrl';

const SkeletonLine = ({ w = '60%', h = 13 }) => (
  <div className="skeleton skeleton-line" style={{ width: w, height: h, borderRadius: 6, marginBottom: 8 }} />
);

const computeDaysLeft = (endDate) => {
  if (!endDate) return null;
  return Math.max(0, Math.ceil((new Date(endDate) - new Date()) / 86400000));
};

const computeProgress = (durationDays, daysLeft) => {
  if (!durationDays || daysLeft === null) return 100;
  const elapsed = durationDays - daysLeft;
  return Math.min(100, Math.max(0, Math.round((elapsed / durationDays) * 100)));
};

const barColor = (daysLeft) => {
  if (daysLeft === null) return '#1a56db';
  if (daysLeft <= 5)    return '#ef4444';
  if (daysLeft <= 14)   return '#f59e0b';
  return '#1a56db';
};

const StatusBadge = ({ isActive, daysLeft }) => {
  if (isActive) {
    const color  = daysLeft === null ? '#059669' : daysLeft <= 5 ? '#dc2626' : daysLeft <= 14 ? '#d97706' : '#1a56db';
    const bg     = daysLeft === null ? '#ecfdf5' : daysLeft <= 5 ? '#fef2f2' : daysLeft <= 14 ? '#fffbeb' : '#eff6ff';
    const border = daysLeft === null ? '#6ee7b7' : daysLeft <= 5 ? '#fca5a5' : daysLeft <= 14 ? '#fde68a' : '#bfdbfe';
    const label  = daysLeft === null ? 'Ongoing' : daysLeft <= 5 ? 'Expiring soon' : 'Active';
    return (
      <span style={{
        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 9999,
        background: bg, color, border: `1px solid ${border}`,
        fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
      }}>
        {label}
      </span>
    );
  }
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 9999,
      background: '#f3f4f6', color: '#9ca3af', border: '1px solid #e5e7eb',
      fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
    }}>
      Expired
    </span>
  );
};

const MedicineCard = ({ med }) => {
  const daysLeft = computeDaysLeft(med.endDate);
  const progress = computeProgress(med.durationDays, daysLeft);
  const color    = barColor(daysLeft);
  const detailText = daysLeft === null
    ? `${med.dosage} · Ongoing`
    : `${med.dosage} · ${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`;

  const startFmt = med.startDate
    ? new Date(med.startDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
  const endFmt = med.endDate
    ? new Date(med.endDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : 'Open-ended';

  return (
    <div style={{
      background: '#fff',
      border: med.isActive ? '1px solid #e8e4ff' : '1px solid #f3f4f6',
      borderRadius: 16,
      padding: '18px 20px',
      boxShadow: med.isActive ? '0 2px 12px rgba(79,70,229,0.06)' : 'none',
      opacity: med.isActive ? 1 : 0.65,
    }}>
      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, flexShrink: 0,
            background: med.isActive ? '#eef2ff' : '#f3f4f6',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
          }}>
            💊
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 14,
              color: '#1e1b4b', marginBottom: 2,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {med.name}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', fontFamily: 'DM Sans, sans-serif' }}>
              {detailText}
            </div>
          </div>
        </div>
        <StatusBadge isActive={med.isActive} daysLeft={daysLeft} />
      </div>

      {/* Progress bar (only for fixed-duration meds) */}
      {med.durationDays && daysLeft !== null && (
        <div style={{ marginBottom: 12 }}>
          <div style={{
            height: 6, background: '#f3f4f6', borderRadius: 9999, overflow: 'hidden',
          }}>
            <div style={{
              height: '100%', width: `${progress}%`,
              background: color, borderRadius: 9999, transition: 'width 0.4s ease',
            }} />
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            fontSize: 10.5, color: '#9ca3af', fontFamily: 'DM Sans, sans-serif', marginTop: 4,
          }}>
            <span>Day 1</span>
            <span>Day {med.durationDays}</span>
          </div>
        </div>
      )}

      {/* Details grid */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px',
        fontSize: 12, fontFamily: 'DM Sans, sans-serif',
      }}>
        <div><span style={{ color: '#9ca3af' }}>Start: </span><span style={{ color: '#374151', fontWeight: 500 }}>{startFmt}</span></div>
        <div><span style={{ color: '#9ca3af' }}>End: </span><span style={{ color: '#374151', fontWeight: 500 }}>{endFmt}</span></div>
        {med.instructions && (
          <div style={{ gridColumn: '1 / -1' }}>
            <span style={{ color: '#9ca3af' }}>Instructions: </span>
            <span style={{ color: '#374151', fontWeight: 500 }}>{med.instructions}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const ActiveMedicines = () => {
  const navigate    = useNavigate();
  const { token }   = useAuth();
  const { tenant }  = useTenant();

  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [filter, setFilter]       = useState('all'); // 'all' | 'active' | 'expired'

  const load = useCallback(async () => {
    if (!token || !tenant?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/patient/medicines`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenant.id,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      setMedicines(Array.isArray(data) ? data : []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [token, tenant?.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = medicines.filter(m => {
    if (filter === 'active')  return m.isActive;
    if (filter === 'expired') return !m.isActive;
    return true;
  });

  const activeCount  = medicines.filter(m => m.isActive).length;
  const expiredCount = medicines.filter(m => !m.isActive).length;

  const FILTERS = [
    { key: 'all',     label: `All (${medicines.length})` },
    { key: 'active',  label: `Active (${activeCount})`   },
    { key: 'expired', label: `Expired (${expiredCount})`  },
  ];

  return (
    <div className="page">
      <main className="page-main">

        {/* Breadcrumb */}
        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <button className="t-link" onClick={() => navigate('/dashboard/patient')}>Dashboard</button>
          <span>›</span>
          <span className="bc-current">My Medicines</span>
        </div>

        {/* Page header */}
        <div className="fade-up" style={{ marginBottom: '1.5rem' }}>
          <h1 className="t-h1" style={{ marginBottom: 4 }}>💊 My Medicines</h1>
          <p className="t-body-sm">Doctor-prescribed medicines from your consultations.</p>
        </div>

        {/* Filter pills */}
        {!loading && !error && medicines.length > 0 && (
          <div className="fade-up-1" style={{ display: 'flex', gap: 8, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
            {FILTERS.map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                style={{
                  padding: '6px 16px', borderRadius: 9999, fontSize: 12.5, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                  border: filter === f.key ? '1.5px solid #c7d2fe' : '1.5px solid #e5e7eb',
                  background: filter === f.key ? '#eef2ff' : '#fff',
                  color: filter === f.key ? '#4f46e5' : '#6b7280',
                  transition: 'all .12s',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="error-state" style={{ marginBottom: '1rem' }}>
            <span>⚠️</span> Could not load medicines.
            <button className="t-link" style={{ marginLeft: 12 }} onClick={load}>Retry</button>
          </div>
        )}

        {/* Skeleton */}
        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[1, 2, 3].map(i => (
              <div key={i} className="card" style={{ padding: '18px 20px' }}>
                <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                  <div className="skeleton" style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <SkeletonLine w="45%" h={14} />
                    <SkeletonLine w="60%" h={12} />
                  </div>
                </div>
                <div className="skeleton" style={{ height: 6, borderRadius: 9999, marginBottom: 12 }} />
                <SkeletonLine w="80%" h={11} />
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && medicines.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">💊</div>
            <p className="empty-title">No medicines yet</p>
            <p className="t-body-sm" style={{ marginTop: 6 }}>
              Medicines prescribed during your consultations will appear here.
            </p>
          </div>
        )}

        {/* No results for filter */}
        {!loading && !error && medicines.length > 0 && filtered.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <p className="empty-title">No {filter} medicines</p>
          </div>
        )}

        {/* Medicine cards */}
        {!loading && !error && filtered.length > 0 && (
          <div className="fade-up-2" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {filtered.map((med, idx) => (
              <div key={med.id || idx} style={{ animation: `fadeUp .3s ${idx * 30}ms ease both` }}>
                <MedicineCard med={med} />
              </div>
            ))}
          </div>
        )}

      </main>
    </div>
  );
};

export default ActiveMedicines;